import React from 'react';
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';

import { formatarMoedaCurta } from '../apresentacao';
import { formatarPercentual } from '../../utils/series';

/**
 * Onde cada real do faturamento foi parar, em uma barra só.
 *
 * As três parcelas fecham em 100% por construção — é a mesma subtração que
 * produz o resultado em competência. Quando o mês fecha no vermelho, a parcela
 * que falta aparece em vermelho DEPOIS dos 100%, em vez de a barra ser
 * normalizada: encolher os custos para caber faria um mês de prejuízo parecer
 * com um mês equilibrado.
 */
const BarraDeComposicao = ({ composicao }) => {
  const theme = useTheme();
  if (!composicao) return null;

  const negativo = composicao.segmentos.some((s) => s.negativo);
  const totalDeCusto = composicao.segmentos
    .filter((s) => s.id !== 'resultado')
    .reduce((soma, s) => soma + s.percentual, 0);
  // Com prejuízo os custos passam de 100%: a régua da barra vira o total real.
  const regua = negativo ? Math.max(totalDeCusto, 100) : 100;

  const CORES = {
    cmv: theme.palette.mode === 'dark' ? '#4b5f78' : '#8fa3b8',
    operacional: theme.palette.primary.main,
    resultado: negativo ? theme.palette.error.main : theme.palette.success.main
  };

  return (
    <Box>
      <Box
        sx={{ display: 'flex', height: 28, borderRadius: 1, overflow: 'hidden', gap: '2px' }}
        role="img"
        aria-label={composicao.segmentos
          .map((s) => `${s.rotulo}: ${formatarPercentual(s.percentual, 1)}`)
          .join('; ')}
      >
        {composicao.segmentos.map((segmento) => (
          <Tooltip
            key={segmento.id}
            title={`${segmento.rotulo}: ${formatarMoedaCurta(Math.abs(segmento.valor))} · ${formatarPercentual(
              segmento.percentual,
              1
            )} do faturamento`}
          >
            <Box
              sx={{
                width: `${Math.max(0, (segmento.percentual / regua) * 100)}%`,
                bgcolor: CORES[segmento.id],
                transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                // Quem pediu menos movimento no sistema nao deve ver a barra crescer.
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
              }}
            />
          </Tooltip>
        ))}
      </Box>

      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} sx={{ mt: 1.5 }}>
        {composicao.segmentos.map((segmento) => (
          <Stack key={segmento.id} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: CORES[segmento.id] }} />
            <Typography variant="caption" color="text.secondary">
              {segmento.rotulo}
            </Typography>
            <Typography variant="caption" className="valor-numerico" sx={{ fontWeight: 600 }}>
              {formatarPercentual(segmento.percentual, 1)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default BarraDeComposicao;
